import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { updateSEOMeta, resetSEOMeta, useBlogLanguage } from '../utils/seoUtils';

const WHATSAPP_LINK = "https://wa.me/529841585732?text=Hola%2C%20quiero%20agendar%20una%20consulta%20gratuita%20con%20Marea%20Riviera";

export default function BlogInversionRivieraMaya() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const es = language === 'es';
  useBlogLanguage('es');

  useEffect(() => {
    updateSEOMeta({
      title: es ? 'Inversión Inmobiliaria en Riviera Maya 2026: Guía Completa | Marea Riviera' : 'Real Estate Investment in Riviera Maya 2026: Complete Guide | Marea Riviera',
      description: es ? 'Todo lo que necesitas saber para invertir en bienes raíces en la Riviera Maya: rendimientos reales, zonas, tipos de propiedad, marco legal y cómo empezar.' : 'Everything you need to know about investing in Riviera Maya real estate: real yields, zones, property types, legal framework and how to get started.',
      url: 'https://mareariviera.com/blog/inversion-inmobiliaria-riviera-maya',
      lang: es ? 'es' : 'en'
    });
    window.scrollTo(0, 0);
    return () => { resetSEOMeta(); };
  }, [es]);

  const scrollToContact = () => { sessionStorage.setItem('scrollTo', 'contact'); navigate('/'); };

  return (
    <div className="bg-[#FAFAF7] min-h-screen pt-[120px]">
      {/* Hero */}
      <div className="bg-[#0A2235] py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#2BBFBF] hover:text-[#7ECFCF] transition-colors mb-8 font-inter text-sm"
            data-testid="blog-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {es ? 'Volver al inicio' : 'Back to home'}
          </button>
          <div className="flex items-center gap-4 text-[#FAFAF7]/50 font-inter text-sm mb-6">
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {es ? '18 mayo 2026' : 'May 18, 2026'}</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {es ? '12 min lectura' : '12 min read'}</span>
          </div>
          <h1 className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-[#FAFAF7] leading-tight mb-6" data-testid="blog-title">
            {es ? 'Inversión Inmobiliaria en Riviera Maya 2026: Guía Completa' : 'Real Estate Investment in Riviera Maya 2026: Complete Guide'}
          </h1>
          <p className="font-inter text-[#FAFAF7]/70 text-lg leading-relaxed">
            {es ? 'Todo lo que necesitas saber para invertir en bienes raíces en la Riviera Maya: rendimientos reales, zonas, tipos de propiedad, marco legal y cómo empezar.' : 'Everything you need to know about investing in Riviera Maya real estate: real yields, zones, property types, legal framework and how to get started.'}
          </p>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="blog-content">
        <div className="prose-marea">
          <p>La Riviera Maya es hoy uno de los mercados inmobiliarios más atractivos de América Latina. No es opinión — es matemática: mientras un depósito bancario en México rinde entre 9% y 11% anual, una propiedad bien seleccionada en esta región puede generar entre 10% y 18% anual, con la ventaja de que el activo se valoriza al mismo tiempo.</p>
          <p>Pero como cualquier inversión, hacerlo bien requiere información. Esta guía cubre todo lo que necesitas saber antes de poner un solo peso — o dólar — en la Riviera Maya. Si ya tienes claro que quieres invertir pero no sabes en qué ciudad, te recomendamos leer nuestra comparativa de <a href="/blog/tulum-vs-playa-del-carmen-donde-invertir" className="text-[#2BBFBF] underline hover:text-[#7ECFCF] transition-colors">Tulum vs Playa del Carmen</a>.</p>

          <h2>¿Por Qué Invertir en la Riviera Maya en 2026?</h2>

          <h3>Un mercado turístico sin freno</h3>
          <p>La Riviera Maya recibe más de 10 millones de turistas al año, con Cancún como el aeropuerto más transitado de México y Tulum consolidándose como destino de lujo internacional. La demanda de hospedaje vacacional supera consistentemente la oferta disponible — lo que se traduce en ocupación alta y tarifas sostenidas.</p>

          <h3>Infraestructura que impulsa la plusvalía</h3>
          <p>En los últimos tres años, la región ha visto tres obras de infraestructura que están transformando el mercado:</p>
          <ul>
            <li><strong>Aeropuerto Internacional de Tulum</strong> (inaugurado 2024) — conecta directamente con EUA, Canadá y Europa</li>
            <li><strong>Tren Maya</strong> — conecta Cancún, Playa del Carmen, Tulum y Mérida en menos de 2 horas</li>
            <li><strong>Autopista Cancún-Tulum ampliada</strong> — reduce tiempos y abre nuevas zonas de desarrollo</li>
          </ul>
          <p>Cada una de estas obras genera plusvalía en los corredores que atraviesa.</p>

          <h3>Demanda de renta vacacional en ascenso</h3>
          <p>Plataformas como Airbnb y Vrbo reportan que la Riviera Maya es consistentemente una de las regiones con mayor demanda en México. La combinación de turismo nacional e internacional genera ocupación durante todo el año — no solo en temporada alta. Conoce más sobre el <a href="/blog/airbnb-tulum-cuanto-rinde-departamento" className="text-[#2BBFBF] underline hover:text-[#7ECFCF] transition-colors">rendimiento Airbnb en Tulum</a>.</p>

          <h2>¿Cuánto Rinde una Propiedad en la Riviera Maya?</h2>
          <p>Esta es la pregunta central. La respuesta depende de tres factores: la zona, el tipo de propiedad y el modelo de renta.</p>

          <h3>ROI estimado por zona</h3>
          <div className="overflow-x-auto my-8">
            <table>
              <thead>
                <tr><th>Zona</th><th>Precio promedio por m²</th><th>ROI bruto anual estimado</th></tr>
              </thead>
              <tbody>
                <tr><td>Tulum (Aldea Zamá, La Veleta)</td><td>$3,000 – $5,500 USD</td><td>10–15%</td></tr>
                <tr><td>Playa del Carmen (Centro, Playacar)</td><td>$2,000 – $4,000 USD</td><td>8–12%</td></tr>
                <tr><td>Puerto Morelos</td><td>$1,800 – $3,000 USD</td><td>7–11%</td></tr>
                <tr><td>Cancún (Zona Hotelera)</td><td>$2,500 – $4,500 USD</td><td>8–13%</td></tr>
              </tbody>
            </table>
          </div>
          <p><em>ROI bruto = ingresos por renta antes de gastos operativos. ROI neto suele ser 2–4 puntos porcentuales menor. Los rangos son estimados de mercado — el rendimiento real depende del desarrollo específico, su operación y temporada.</em></p>

          <h3>Renta vacacional vs. renta de largo plazo</h3>
          <p><strong>Renta vacacional (Airbnb/Vrbo):</strong></p>
          <ul>
            <li>Mayor ingreso por noche</li>
            <li>Ocupación estacional (picos en dic-abr y jul-ago)</li>
            <li>Requiere administración activa o administradora</li>
            <li>ROI bruto estimado: 8–15% dependiendo zona y temporada</li>
          </ul>
          <p><strong>Renta de largo plazo:</strong></p>
          <ul>
            <li>Ingreso mensual fijo y predecible</li>
            <li>Menor ROI pero mayor estabilidad</li>
            <li>Ideal para propiedades en zonas residenciales o con alta población de nómadas digitales</li>
            <li>ROI bruto: 6–9% anual</li>
          </ul>
          <p>La mayoría de los inversionistas en la región operan con <strong>modelo mixto</strong>: renta vacacional en temporada alta y contratos de 3–6 meses en temporada baja.</p>

          <h2>Tipos de Propiedad: ¿En Qué Invertir?</h2>

          <h3>Departamento en desarrollo turístico</h3>
          <p>El formato más común para inversión. Desarrollos diseñados específicamente para renta vacacional, con amenidades (alberca, gym, coworking) que aumentan la tarifa por noche. Generalmente se venden en preventa con precios 15–25% menores al valor de entrega.</p>
          <p><strong>Perfil:</strong> Inversionista pasivo que busca ROI sin gestión propia.</p>

          <h3>Departamento residencial</h3>
          <p>En zonas más consolidadas como Playa del Carmen o Puerto Morelos. Ideal para renta de largo plazo o como segunda residencia con opción de renta parcial.</p>
          <p><strong>Perfil:</strong> Inversionista conservador o comprador mixto (uso propio + renta).</p>

          <h3>Terreno</h3>
          <p>Mayor riesgo pero mayor potencial de plusvalía. Requiere capital para desarrollo posterior o paciencia para valorización pasiva.</p>
          <p><strong>Perfil:</strong> Inversionista con horizonte de 5–10 años y tolerancia al riesgo.</p>

          <h2>Marco Legal: Lo Que Todo Extranjero Debe Saber</h2>
          <p>Un punto que genera dudas, especialmente entre inversionistas internacionales: <strong>¿pueden los extranjeros comprar propiedades en México?</strong></p>
          <p>La respuesta es sí — con una estructura legal específica.</p>

          <h3>Zona Restringida y Fideicomiso</h3>
          <p>La Constitución Mexicana establece una "zona restringida" de 50 km desde la costa y 100 km desde fronteras internacionales. Los extranjeros no pueden poseer tierra en esta zona de forma directa, pero sí pueden hacerlo a través de un <strong>fideicomiso bancario</strong>.</p>
          <p>El fideicomiso es un contrato con un banco mexicano que actúa como fiduciario. El banco tiene el título legal, pero el extranjero tiene todos los derechos: habitar, rentar, vender, heredar. Es un instrumento completamente legal y ampliamente utilizado.</p>
          <p><strong>Costo:</strong> Aproximadamente $500–$800 USD anuales de mantenimiento del fideicomiso.</p>

          <h3>Personas Morales (para mexicanos y extranjeros)</h3>
          <p>Otra opción es adquirir la propiedad a través de una sociedad mexicana (S.A. de C.V. o S.A.P.I.). Esto tiene ventajas fiscales para quienes van a operar renta vacacional de forma profesional.</p>

          <h3>Impuestos y Costos de Cierre</h3>
          <p>Al comprar una propiedad en México, los costos de cierre típicos son:</p>
          <ul>
            <li><strong>ISR (Impuesto sobre la Renta):</strong> 2–5% del valor catastral (varía por estado)</li>
            <li><strong>Derechos de registro:</strong> ~1% del valor</li>
            <li><strong>Honorarios notariales:</strong> ~1–2% del valor</li>
            <li><strong>Total costos de cierre estimados:</strong> 4–8% del precio de compra</li>
          </ul>

          <h2>¿Cómo Empezar? Pasos para Tu Primera Inversión</h2>

          <h3>1. Define tu perfil de inversionista</h3>
          <ul>
            <li>¿Cuánto capital tienes disponible?</li>
            <li>¿Buscas flujo mensual o plusvalía a largo plazo?</li>
            <li>¿Quieres gestión pasiva o puedes involucrarte en la operación?</li>
            <li>¿Cuál es tu horizonte de inversión? (2 años, 5 años, 10 años)</li>
          </ul>

          <h3>2. Elige la zona correcta</h3>
          <p>No todas las zonas son iguales ni sirven para todos los objetivos. Tulum maximiza plusvalía. Playa del Carmen ofrece estabilidad. Si buscas menor precio de entrada con alto potencial, te recomendamos <a href="/blog/invertir-puerto-morelos-riviera-maya" className="text-[#2BBFBF] underline hover:text-[#7ECFCF] transition-colors">invertir en Puerto Morelos</a>.</p>

          <h3>3. Evalúa el desarrollo con due diligence</h3>
          <p>Antes de firmar cualquier cosa, verifica:</p>
          <ul>
            <li><strong>MIA vigente</strong> (Manifestación de Impacto Ambiental) — sin esto, el desarrollo no puede construirse legalmente</li>
            <li><strong>Escrituras limpias</strong> — sin gravámenes ni litigios</li>
            <li><strong>Track record del desarrollador</strong> — proyectos entregados anteriores</li>
            <li><strong>Proyecciones de renta realistas</strong> — no promesas infladas</li>
          </ul>

          <h3>4. Estructura la operación correctamente</h3>
          <p>Define desde el inicio si vas a comprar como persona física o moral, si necesitas fideicomiso (si eres extranjero), y cómo vas a operar la renta.</p>

          <h3>5. Trabaja con un broker especializado</h3>
          <p>Un broker que conozca el mercado local puede ahorrarte errores costosos: sobre-pagar, elegir la zona equivocada, o caer en desarrollos con problemas legales.</p>

          <h2>Errores Comunes al Invertir en la Riviera Maya</h2>

          <p><strong>Comprar por precio sin analizar la zona</strong></p>
          <p>El metro cuadrado más barato no siempre es la mejor inversión. Una propiedad en zona sin demanda turística comprobada puede tener ROI mínimo independientemente del precio.</p>

          <p><strong>No verificar la MIA</strong></p>
          <p>Desarrollos sin MIA vigente son un riesgo legal real. Hemos visto proyectos paralizados por años por este motivo.</p>

          <p><strong>Confiar en proyecciones de renta sin sustento</strong></p>
          <p>Algunos desarrolladores proyectan ocupaciones del 90% anual. La realidad del mercado para una propiedad nueva es 55–70% el primer año, creciendo conforme se posiciona en plataformas.</p>

          <p><strong>Ignorar los costos operativos</strong></p>
          <p>La administradora cobra 20–30% de los ingresos por renta. Súmale mantenimiento, cuotas de condominio, impuestos y el ROI neto puede ser significativamente menor al bruto.</p>

          <h2>La Riviera Maya en 2026: ¿Es Buen Momento para Invertir?</h2>
          <p>Los indicadores apuntan que sí. La infraestructura recién inaugurada está generando una nueva ola de valorización. Los precios en Tulum, aunque más altos que hace 5 años, aún tienen margen de crecimiento frente a destinos comparables internacionalmente. Y la demanda turística no muestra señales de desaceleración.</p>
          <p>Lo que sí es diferente respecto a hace 5 años: <strong>la selección es más importante</strong>. En un mercado maduro, la diferencia entre una buena y una mala inversión ya no es el destino — es el desarrollo, la zona específica y la estructura de la operación.</p>

          <h2>Preguntas Frecuentes sobre Inversión Inmobiliaria en Riviera Maya</h2>

          <p><strong>¿Cuánto se necesita para invertir en Riviera Maya?</strong></p>
          <p>El ticket de entrada varía según la zona y tipo de propiedad. En Puerto Morelos puedes encontrar departamentos en preventa desde 150,000 USD. En Tulum y Playa del Carmen el rango más común para inversión con renta vacacional es de 180,000 a 350,000 USD.</p>

          <p><strong>¿Qué rendimiento anual genera una propiedad en Riviera Maya?</strong></p>
          <p>Una propiedad bien ubicada y gestionada genera entre 8% y 12% de rendimiento anual en renta vacacional. La plusvalía adicional en zonas en desarrollo puede sumar entre 8% y 15% anual.</p>

          <p><strong>¿Pueden los extranjeros comprar propiedades en Riviera Maya?</strong></p>
          <p>Sí. Los extranjeros adquieren la propiedad a través de un fideicomiso bancario (bank trust), que les otorga todos los derechos de uso, renta y venta del inmueble.</p>

          <p><strong>¿Cuál es la mejor zona para invertir en Riviera Maya en 2026?</strong></p>
          <p>Depende del perfil de inversión: Tulum ofrece mayor plusvalía y renta vacacional premium; Playa del Carmen combina liquidez y demanda constante; Puerto Morelos es la opción emergente con menor precio de entrada y alta apreciación proyectada.</p>

          <p><strong>¿Cuánto tiempo tarda en pagarse una inversión inmobiliaria en Riviera Maya?</strong></p>
          <p>Con un manejo eficiente de renta vacacional, el periodo de recuperación estimado es de 8 a 12 años, combinando flujo de renta más plusvalía acumulada.</p>
        </div>

        {/* CTA */}
        <div className="mt-16 bg-[#0A2235] rounded-[20px] p-8 md:p-12 text-center">
          <h2 className="font-playfair text-2xl md:text-3xl text-[#FAFAF7] mb-4">
            {es ? '¿Listo para Dar el Siguiente Paso?' : 'Ready to Take the Next Step?'}
          </h2>
          <p className="font-inter text-[#FAFAF7]/70 mb-8 max-w-xl mx-auto leading-relaxed">
            {es ? 'En Marea Riviera analizamos tu perfil de inversionista y te presentamos opciones concretas — con proyecciones de ROI reales, due diligence incluido y acompañamiento durante todo el proceso.' : 'At Marea Riviera we analyze your investor profile and present concrete options — with real ROI projections, included due diligence, and support throughout the buying process.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-[#2BBFBF] hover:bg-[#7ECFCF] text-[#0A2235] font-medium rounded-full px-8 py-6 text-base"
              data-testid="blog-cta-whatsapp"
            >
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                {es ? 'Agenda una consulta gratuita' : 'Schedule a free consultation'}
              </a>
            </Button>
            <Button
              onClick={scrollToContact}
              variant="outline"
              className="border-[#2BBFBF] text-[#2BBFBF] hover:bg-[#2BBFBF] hover:text-[#0A2235] rounded-full px-8 py-6 text-base"
              data-testid="blog-cta-contact"
            >
              {es ? 'Ir al formulario de contacto' : 'Go to contact form'}
            </Button>
          </div>
          <p className="font-inter text-[#FAFAF7]/40 text-sm mt-6">{es ? 'Sin compromiso. Respuesta en menos de 2 horas.' : 'No commitment. Response within 2 hours.'}</p>
        </div>

        {/* Author */}
        <div className="mt-12 pt-8 border-t border-[#0A2235]/10">
          <p className="font-inter text-[#0A2235]/50 text-sm italic leading-relaxed">
            Marea Riviera es una empresa de inversión inmobiliaria especializada en Tulum, Playa del Carmen y Puerto Morelos. Trabajamos exclusivamente con desarrollos que cuentan con MIA vigente y estructura legal sólida.
          </p>
        </div>
      </article>
    </div>
  );
}
